import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import usePersonalWebsite from './hooks/usePersonalWebsite';
import { CaretRight, CaretLeft, Star, ListBullets, Palette } from '@phosphor-icons/react';

import ColorInit from '../helper/ColorInit';
import ScrollToTop from 'react-scroll-to-top';
import Preloader from '../helper/Preloader';

import TemplateHeader from './components/TemplateHeader';
import TemplateFooter from './components/TemplateFooter';

import { dummyStoreInfo, dummyCategories, dummyProducts } from './dummyData';
import TemplateBreadcrumb from './components/TemplateBreadcrumb';
import TemplateBanner from './components/TemplateBanner';
import TemplateCategoryNav from './components/TemplateCategoryNav';
import TemplateYouMightLike from './components/TemplateYouMightLike';
import TemplateLargeBanner from './components/TemplateLargeBanner';
import TemplateDescription from './components/TemplateDescription';
import TemplateProductCategory from './components/TemplateProductCategory';
import ThemeSelectionModal from './components/ThemeSelectionModal';
import { theme as websiteThemes } from './dummyData';

const TemplatePage = () => {
    const [isThemeModalOpen, setIsThemeModalOpen] = useState(false);
    const { fetchPersonalWebsite, data: websiteData, status: websiteStatus } = usePersonalWebsite();

    useEffect(() => {
        const currentDomain = window.location.hostname;
        fetchPersonalWebsite({ url: currentDomain });
    }, []);
    const [activeThemeId, setActiveThemeId] = useState(localStorage.getItem('website_theme') || 'default');

    const templateTypeIndex = websiteData?.templateType;
    let activeThemeColors;

    if (templateTypeIndex !== undefined && templateTypeIndex !== null && websiteThemes[templateTypeIndex]) {
        activeThemeColors = websiteThemes[templateTypeIndex].colors;
    } else {
        activeThemeColors = websiteThemes.find((t) => t.name === activeThemeId)?.colors || websiteThemes[0].colors;
    }

    useEffect(() => {
        document.title = 'Template - ' + (websiteData?.shopName || dummyStoreInfo.businessName);
    }, [websiteData]);

    const [mobileTab, setMobileTab] = useState('home');

    return (
        <div style={{ backgroundColor: '#ffffff', minHeight: '100vh' }}>
            <ColorInit color={false} />
            <ScrollToTop smooth color="#000000" />
            <Preloader />

            <TemplateHeader colors={activeThemeColors} websiteData={websiteData} />

            <TemplateBreadcrumb colors={activeThemeColors} />

            {/* Store Banner */}
            <TemplateBanner websiteData={websiteData} />

            {/* Desktop View */}
            <div className="d-none d-md-block">
                {/* Category Sub Nav */}
                <TemplateCategoryNav colors={activeThemeColors} websiteData={websiteData} />

                {/* You Might Like */}
                <TemplateYouMightLike websiteData={websiteData} />

                {/* Large Banner */}
                <TemplateLargeBanner />

                {/* Store Description - Full Width */}
                <TemplateDescription websiteData={websiteData} />

                {/* Category + Sort + Product Grid */}
                <TemplateProductCategory colors={activeThemeColors} websiteData={websiteData} />
            </div>

            {/* Mobile View */}
            <div className="d-block d-md-none">
                {/* Mobile Tabs */}
                <div className="container mt-3 mb-20">
                    <div className="d-flex justify-content-start border-bottom">
                        {['home', 'product', 'category'].map((tab) => (
                            <Link
                                key={tab}
                                to="#"
                                className="text-decoration-none text-capitalize"
                                style={{
                                    padding: '0.5rem 1rem',
                                    color: mobileTab === tab ? '#000' : '#828282',
                                    borderBottom: mobileTab === tab ? '2px solid #000' : '0',
                                    fontWeight: '600'
                                }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setMobileTab(tab);
                                }}
                            >
                                {tab}
                            </Link>
                        ))}
                    </div>
                </div>

                {mobileTab === 'home' && (
                    <>
                        <TemplateYouMightLike websiteData={websiteData} />
                        <TemplateLargeBanner />
                        <TemplateDescription websiteData={websiteData} />
                    </>
                )}
                {mobileTab === 'product' && (
                    <TemplateProductCategory colors={activeThemeColors} isMobileProduct={true} websiteData={websiteData} />
                )}
                {mobileTab === 'category' && (
                    <TemplateCategoryNav colors={activeThemeColors} isMobileView={true} websiteData={websiteData} />
                )}
            </div>

            <TemplateFooter colors={activeThemeColors} websiteData={websiteData} />

            {/* Floating Action Button for Theme Selection */}
            <button
                className="btn btn-primary rounded-circle shadow d-flex align-items-center justify-content-center"
                style={{
                    position: 'fixed',
                    bottom: '32px',
                    left: '32px',
                    width: '56px',
                    height: '56px',
                    zIndex: 1000,
                    backgroundColor: '#2563EB',
                    border: 'none'
                }}
                onClick={() => setIsThemeModalOpen(true)}
                title="Theme Settings"
            >
                <Palette size={28} color="#FFFFFF" weight="fill" />
            </button>

            <ThemeSelectionModal
                open={isThemeModalOpen}
                onClose={() => setIsThemeModalOpen(false)}
                onThemeChange={(newTheme) => setActiveThemeId(newTheme)}
            />
        </div>
    );
};

export default TemplatePage;
